import React, { useEffect, useRef, useState } from 'react';

interface SplitTextProps {
  text: string;
  className?: string;
  animation?: 'fade-up' | 'fade-in' | 'slide-up';
  delay?: number;
  duration?: number;
  stagger?: number;
}

const SplitText: React.FC<SplitTextProps> = ({
  text,
  className = '',
  animation = 'fade-up',
  delay = 0,
  duration = 0.4,
  stagger = 0.1
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setIsVisible(true);
          }, delay);
        }
      },
      { threshold: 0.1 }
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observer.unobserve(currentContainer);
      }
    };
  }, [delay]);

  const words = text.split(' ');

  const getAnimationStyles = (index: number) => {
    const baseStyles = {
      transition: `all ${duration}s ease-out`,
      transitionDelay: `${index * stagger}s`,
    };

    if (!isVisible) {
      switch (animation) {
        case 'fade-up':
          return {
            ...baseStyles,
            opacity: 0,
            transform: 'translateY(20px)',
          };
        case 'fade-in':
          return {
            ...baseStyles,
            opacity: 0,
          };
        case 'slide-up':
          return {
            ...baseStyles,
            transform: 'translateY(100%)',
          };
        default:
          return baseStyles;
      }
    }

    return {
      ...baseStyles,
      opacity: 1,
      transform: 'translateY(0)',
    };
  };

  return (
    <div ref={containerRef} className={className}>
      {words.map((word, index) => (
        <span
          key={index}
          style={getAnimationStyles(index)}
          className="inline-block"
        >
          {word}
          {index < words.length - 1 && '\u00A0'}
        </span>
      ))}
    </div>
  );
};

export default SplitText;
